import type { PnlTieLine, ServedChannel, ServedTie } from "./api";

const CHANNEL_TITLE: Record<string, string> = { amazon: "Amazon", shopify: "Shopify and Wholesale", other: "Other channel costs" };
const COMPONENT_NAME: Record<string, string> = {
  pick_pack: "Pick & Pack", packaging: "Packaging", courier: "Courier",
  referral: "Referral fee", fba_per_unit: "FBA fee", dsf: "Digital services fee",
};
const SERVED_REASON: Record<string, string> = {
  not_closed: "Not closed in PFC yet, so there is no Xero tie for this month. Cost History shows these amounts.",
  no_month_rows: "Closed in PFC, but the close has no channel account rows for this month, so there is no Xero tie. Cost History shows these amounts.",
  no_live_xero: "Live Xero returned nothing for this month, so there is no tie. Cost History shows these amounts.",
};

export function channelTitle(channel: string): string { return CHANNEL_TITLE[channel] ?? channel; }

export function servedLineLabel(line: PnlTieLine, channel: string): string {
  const name = line.account_name ?? "";
  switch (line.kind) {
    case "xero_changed_since_close": return `Xero changed since the close: ${name}`;
    case "xero_not_in_close": return `In Xero, not in the close: ${name}`;
    case "refund_credits": return `Refund fee credits (Xero nets them, Cost History shows fees before refunds): ${name}`;
    case "close_billing_differs": return channel === "shopify"
      ? `Billed orders Cost History leaves out (not complete, or unknown SKU): ${name}`
      : `Billing differs from the close (FX rounding, marketplace mapping or load timing): ${name}`;
    case "not_spread": return line.tie_status === "accepted" ? `Accepted difference, not spread: ${name}`
      : line.tie_status === "flagged" ? `Open flag, not spread: ${name}` : `Not spread: ${name}`;
    case "billed_no_account": return `Billed with no Xero account this month: ${COMPONENT_NAME[line.component ?? ""] ?? line.component}`;
    case "allocated_no_account": return `Allocated to an account Xero does not have this month: ${name}`;
    case "allocated_cogs_account": return `Cost of Goods Sold account allocated in Cost History (its P&L line is in Sold Costs): ${name}`;
    default: return `Other difference (${line.kind})${name ? `: ${name}` : ""}`;
  }
}

export function servedReason(data: ServedTie): string | null {
  if (data.reason === null) return null;
  if (data.reason === "versions_differ") return `The close and its allocation are on different versions (${data.versions.join(", ")}), so there is no Xero tie until they match. Cost History shows these amounts.`;
  return SERVED_REASON[data.reason] ?? `No Xero tie for this month (${data.reason}). Cost History shows these amounts.`;
}

// A channel with nothing in it is dropped; the served strings are compared as strings, no arithmetic.
export function servedChannels(data: ServedTie): ServedChannel[] {
  return data.channels.filter((channel) => !(channel.shown === "0.0000" && (channel.xero_live === null || channel.xero_live === "0.0000") && channel.lines.length === 0));
}
